/**
 * ComparisonTab — so sánh current PO với comparison PO + narrative + market
 * + production summary (spec §6 — `Excel_Production_Report_Web_UI_Conversion_Spec.md`).
 *
 * Áp skill `/table-style`: header gradient via `app-table-header-cell`,
 * cell ellipsis via `app-table-cell-text`. Variance dùng color text only.
 *
 * Sections:
 *   1. Yield comparison by item (table).
 *   2. FAQ quality comparison (table).
 *   3. Narrative quality notes (table).
 *   4. Price info + Production info (key-value panels).
 */
import {
  Box,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'

import {
  FAQ_QUALITY_COMPARE_ROWS,
  NARRATIVE_NOTE_ROWS,
  PRICE_INFO,
  PRODUCTION_INFO,
  SAMPLE_COMPARISON_PO_NO,
  SAMPLE_PO_NO,
  YIELD_COMPARISON_ROWS,
} from '../productionReportMock'
import { fmtMt, fmtPercentScaled, fmtPlain, fmtUsd, fmtVarPct } from './formatters'
import {
  COLORS,
  neutralChipSx,
  sectionCardSx,
  sectionHeaderSx,
  sectionSubtitleSx,
  sectionTitleSx,
  varianceColor,
} from './styleTokens'

interface Props {
  poNo: string
}

/* ---------- Section: Yield comparison by item --------------------------- */

function YieldComparison({ poNo }: { poNo: string }) {
  const rows = YIELD_COMPARISON_ROWS
  const totals = rows.reduce(
    (acc, r) => ({
      previous: acc.previous + r.previous,
      current: acc.current + r.current,
      variance: acc.variance + r.variance,
    }),
    { previous: 0, current: 0, variance: 0 },
  )
  return (
    <Stack spacing={0.75}>
      <Stack spacing={0.25}>
        <Typography sx={sectionTitleSx}>So sánh thu hồi của mẻ</Typography>
        <Typography sx={sectionSubtitleSx}>
          Comparing {SAMPLE_COMPARISON_PO_NO} → {poNo}
        </Typography>
      </Stack>
      <Box className="app-dataframe app-dataframe--compact">
        <Box className="app-table-wrap">
          <Table size="small" stickyHeader sx={{ tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                <TableCell className="app-table-header-cell" style={{ width: 110, minWidth: 110, maxWidth: 110 }}>Item</TableCell>
                <TableCell className="app-table-header-cell" style={{ width: 120, minWidth: 120, maxWidth: 120 }}>Category</TableCell>
                <TableCell align="right" className="app-table-header-cell" style={{ width: 90, minWidth: 90, maxWidth: 90 }}>Premium</TableCell>
                <TableCell align="right" className="app-table-header-cell" style={{ width: 130, minWidth: 130, maxWidth: 130 }}>{SAMPLE_COMPARISON_PO_NO.slice(-5)}</TableCell>
                <TableCell align="right" className="app-table-header-cell" style={{ width: 130, minWidth: 130, maxWidth: 130 }}>{poNo.slice(-5)}</TableCell>
                <TableCell align="right" className="app-table-header-cell" style={{ width: 100, minWidth: 100, maxWidth: 100 }}>Var.</TableCell>
                <TableCell align="right" className="app-table-header-cell" style={{ width: 110, minWidth: 110, maxWidth: 110 }}>Var. (norm)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.item}>
                  <TableCell><Cell value={r.item} /></TableCell>
                  <TableCell><Cell value={r.category} /></TableCell>
                  <TableCell align="right">{fmtUsd(r.premium)}</TableCell>
                  <TableCell align="right">{fmtPercentScaled(r.previous, 2)}</TableCell>
                  <TableCell align="right">{fmtPercentScaled(r.current, 2)}</TableCell>
                  <TableCell align="right" sx={{ color: varianceColor(r.variance), fontWeight: 600 }}>{fmtVarPct(r.variance, 2)}</TableCell>
                  <TableCell align="right" sx={{ color: varianceColor(r.varianceNorm) }}>{fmtVarPct(r.varianceNorm, 1)}</TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell sx={totalCellSx}>TOTAL</TableCell>
                <TableCell sx={totalCellSx} />
                <TableCell sx={totalCellSx} />
                <TableCell align="right" sx={totalCellSx}>{fmtPercentScaled(totals.previous, 2)}</TableCell>
                <TableCell align="right" sx={totalCellSx}>{fmtPercentScaled(totals.current, 2)}</TableCell>
                <TableCell align="right" sx={{ ...totalCellSx, color: varianceColor(totals.variance) }}>{fmtVarPct(totals.variance, 2)}</TableCell>
                <TableCell sx={totalCellSx} />
              </TableRow>
            </TableBody>
          </Table>
        </Box>
      </Box>
    </Stack>
  )
}

const totalCellSx = {
  fontWeight: 700,
  whiteSpace: 'nowrap',
  bgcolor: COLORS.bgSubtle,
  borderTop: `1px solid ${COLORS.borderStrong}`,
  borderBottom: 'none',
}

function Cell({ value }: { value: string | number }) {
  const s = String(value)
  return (
    <Box component="span" className="app-table-cell-text" sx={{ maxWidth: 'none' }} title={s}>
      {s}
    </Box>
  )
}

/* ---------- Section: FAQ quality comparison ----------------------------- */

function FaqQualityCompare({ poNo }: { poNo: string }) {
  const rows = FAQ_QUALITY_COMPARE_ROWS
  const cols: { key: keyof (typeof rows)[number]; label: string }[] = [
    { key: 'mc', label: 'MC' },
    { key: 'blacks', label: 'Blacks' },
    { key: 'brokens', label: 'Brokens' },
    { key: 'bb', label: 'BB' },
    { key: 'fm', label: 'FM' },
    { key: 'mouldy', label: 'Mouldy' },
    { key: 'brown', label: 'Brown' },
    { key: 'partlyBlack', label: 'Partly Black' },
    { key: 'immature', label: 'Immature' },
    { key: 's18', label: 'S18' },
    { key: 's16', label: 'S16' },
    { key: 's13', label: 'S13' },
  ]
  return (
    <Stack spacing={0.75}>
      <Stack spacing={0.25}>
        <Typography sx={sectionTitleSx}>So sánh chất lượng FAQ</Typography>
        <Typography sx={sectionSubtitleSx}>
          Comparing {SAMPLE_COMPARISON_PO_NO} → {poNo}
        </Typography>
      </Stack>
      <Box className="app-dataframe app-dataframe--compact">
        <Box className="app-table-wrap">
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell className="app-table-header-cell" style={{ width: 160 }}>PO</TableCell>
                {cols.map((c) => (
                  <TableCell key={String(c.key)} align="right" className="app-table-header-cell" style={{ width: 80 }}>
                    {c.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r, idx) => {
                const isVar = r.po === 'Variance'
                return (
                  <TableRow key={idx}>
                    <TableCell sx={{ fontWeight: isVar ? 700 : 600 }}>{r.po}</TableCell>
                    {cols.map((c) => {
                      const v = Number(r[c.key]) || 0
                      return (
                        <TableCell
                          key={String(c.key)}
                          align="right"
                          sx={{
                            color: isVar ? varianceColor(v) : undefined,
                            fontWeight: isVar ? 600 : undefined,
                          }}
                        >
                          {isVar ? (v >= 0 ? '+' : '') + fmtPlain(v, 2) : fmtPlain(v, 2)}
                        </TableCell>
                      )
                    })}
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </Box>
      </Box>
    </Stack>
  )
}

/* ---------- Section: Narrative notes ------------------------------------ */

function NarrativeNotes() {
  const rows = NARRATIVE_NOTE_ROWS
  return (
    <Stack spacing={0.75}>
      <Stack spacing={0.25}>
        <Typography sx={sectionTitleSx}>Thông tin chất lượng thành phẩm sau khi chạy máy</Typography>
        <Typography sx={sectionSubtitleSx}>Notes per output item with standardization status.</Typography>
      </Stack>
      <Box className="app-dataframe app-dataframe--compact">
        <Box className="app-table-wrap">
          <Table size="small" stickyHeader sx={{ tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                <TableCell className="app-table-header-cell" style={{ width: 110 }}>Item</TableCell>
                <TableCell className="app-table-header-cell" style={{ width: 140 }}>MC</TableCell>
                <TableCell className="app-table-header-cell" style={{ width: 140 }}>BB</TableCell>
                <TableCell className="app-table-header-cell" style={{ width: 140 }}>FM</TableCell>
                <TableCell className="app-table-header-cell" style={{ width: 110 }}>S18</TableCell>
                <TableCell className="app-table-header-cell" style={{ width: 110 }}>S16</TableCell>
                <TableCell className="app-table-header-cell" style={{ width: 110 }}>S13</TableCell>
                <TableCell className="app-table-header-cell" style={{ width: 340 }}>Ghi chú</TableCell>
                <TableCell align="center" className="app-table-header-cell" style={{ width: 110 }}>Qui chuẩn</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.item}>
                  <TableCell sx={{ fontWeight: 600 }}>{r.item}</TableCell>
                  <TableCell sx={{ whiteSpace: 'normal' }}>{r.mc}</TableCell>
                  <TableCell sx={{ whiteSpace: 'normal' }}>{r.bb}</TableCell>
                  <TableCell sx={{ whiteSpace: 'normal' }}>{r.fm}</TableCell>
                  <TableCell>{r.s18}</TableCell>
                  <TableCell>{r.s16}</TableCell>
                  <TableCell>{r.s13}</TableCell>
                  <TableCell sx={{ whiteSpace: 'normal', color: COLORS.textMuted }}>{r.note}</TableCell>
                  <TableCell align="center">
                    <Chip
                      size="small"
                      label={r.standardized}
                      sx={{
                        height: 20,
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        bgcolor: r.standardized === 'Yes' ? COLORS.successSoft : COLORS.bgHeader,
                        color: r.standardized === 'Yes' ? COLORS.success : COLORS.textMuted,
                        border: 'none',
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Box>
    </Stack>
  )
}

/* ---------- Section: Price info + Production info ----------------------- */

function PriceProductionPanels() {
  const p = PRICE_INFO
  const pr = PRODUCTION_INFO
  return (
    <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
      <Box sx={{ ...sectionCardSx, flex: '1 1 320px', minWidth: 280 }}>
        <Box sx={sectionHeaderSx}>
          <Typography sx={sectionTitleSx}>Thông tin về giá</Typography>
          <Chip size="small" label="Market" sx={neutralChipSx} />
        </Box>
        <Box sx={{ p: 2 }}>
          <InfoRow label="LIFFE" value={fmtUsd(p.liffe)} />
          <InfoRow label="Diff" value={fmtUsd(p.diff)} />
          <InfoRow label="Premium vs G2 (graded)" value={fmtUsd(p.premiumVsG2)} last />
        </Box>
      </Box>
      <Box sx={{ ...sectionCardSx, flex: '1 1 320px', minWidth: 280 }}>
        <Box sx={sectionHeaderSx}>
          <Typography sx={sectionTitleSx}>Thông tin sản xuất</Typography>
          <Chip size="small" label="Production" sx={neutralChipSx} />
        </Box>
        <Box sx={{ p: 2 }}>
          <InfoRow label="Total input" value={`${fmtMt(pr.totalInput)} Mt`} />
          <InfoRow label="Total out-turn" value={fmtPercentScaled(pr.totalOutturn, 2)} tone="success" />
          <InfoRow label="Hao hụt (Loss)" value={fmtPercentScaled(pr.loss, 2)} tone="danger" />
          <InfoRow label="MC loss" value={fmtPercentScaled(pr.mcLoss, 2)} />
          <InfoRow label="Unexplained loss" value={fmtPercentScaled(pr.unexplainedLoss, 2)} tone="danger" last />
        </Box>
      </Box>
    </Stack>
  )
}

function InfoRow({
  label,
  value,
  tone,
  last,
}: { label: string; value: string; tone?: 'danger' | 'success'; last?: boolean }) {
  const color = tone === 'danger' ? COLORS.danger : tone === 'success' ? COLORS.success : COLORS.text
  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      sx={{ py: 1, borderBottom: last ? 'none' : `1px solid ${COLORS.border}` }}
    >
      <Typography sx={{ fontSize: '0.8125rem', color: COLORS.textMuted }}>{label}</Typography>
      <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, color }}>{value}</Typography>
    </Stack>
  )
}

/* ---------- main tab ----------------------------------------------------- */

export default function ComparisonTab({ poNo }: Props) {
  const effectivePo = poNo || SAMPLE_PO_NO
  return (
    <Stack spacing={2}>
      <YieldComparison poNo={effectivePo} />
      <FaqQualityCompare poNo={effectivePo} />
      <NarrativeNotes />
      <PriceProductionPanels />
    </Stack>
  )
}
